import { ChangeEvent, useState } from 'react';
import { FormikProps } from 'formik';
import * as yup from 'yup';
import useApiFetch from '@/hooks/useApiFetch';
import candidatesServices from '@/services/candidates';
import jobsServices, { EditableJob } from '@/services/jobs';
import { FetchApiIds, Question } from '@/types';
import { jobSchema } from '@/utils/jobs';
import JobBasicInfoComponent, { JobBasicInfoComponentProps } from './JobBasicInfo.component';

type JobBasicInfoProps = Pick<
  JobBasicInfoComponentProps,
  'job' | 'workingTimes' | 'handleChangeWorkingTimes' | 'onAddNewWorkingTime' | 'onSubmit'
> & {
  updatedField: string | undefined;
  setUpdatedField: (field: string) => void;
};

const JobBasicInfo = ({
  job,
  workingTimes,
  updatedField,
  onSubmit,
  setUpdatedField,
  handleChangeWorkingTimes,
  onAddNewWorkingTime,
}: JobBasicInfoProps): JSX.Element => {
  const [touchedFields, setTouchedFields] = useState(new Set(''));
  const [errorMessages, setErrorMessages] = useState({});

  const allTechStacksResponse = useApiFetch(
    FetchApiIds.TECH_STACKS,
    candidatesServices.getAllTechsStacks
  );

  const allPartnerAccountsResponse = useApiFetch(
    FetchApiIds.PARTNER_ACCOUNTS,
    jobsServices.getAllPartners
  );

  const techStacksOptions = (allTechStacksResponse.data ?? []).map((tech) => ({
    label: tech?.name,
    value: tech?.id,
  }));

  const partnerOptions = (allPartnerAccountsResponse.data ?? []).map((account) => ({
    label: account.name,
    value: account.id as string,
  }));

  const handleChangeAndMaybeSubmit = (
    form: FormikProps<EditableJob>,
    field: Question,
    event: ChangeEvent<HTMLInputElement>,
    submitForm?: boolean
  ): void => {
    form.handleChange(event);
    setUpdatedField(field.name);
    setTouchedFields((prev) => prev.add(field.name));
    if (submitForm) {
      form.handleSubmit();
    }
  };

  const submitFormikForm = async (values: EditableJob): Promise<void> => {
    if (!updatedField) {
      return;
    }

    try {
      const updatedValue = values[updatedField];
      await yup.reach(jobSchema, updatedField).validate(updatedValue);
    } catch (error: any) {
      if (error instanceof yup.ValidationError) {
        setErrorMessages((prev) => ({
          ...prev,
          [updatedField]: error.message,
        }));
        return;
      }
    }

    await onSubmit(values);
    setUpdatedField('');
    setErrorMessages((prev) => ({
      ...prev,
      [updatedField]: '',
    }));
  };

  return (
    <JobBasicInfoComponent
      errorMessages={errorMessages}
      techStacksOptions={techStacksOptions}
      partnerOptions={partnerOptions}
      touchedFields={[...touchedFields.values()].filter(Boolean)}
      job={job}
      workingTimes={workingTimes}
      handleChangeWorkingTimes={handleChangeWorkingTimes}
      onAddNewWorkingTime={onAddNewWorkingTime}
      onSubmit={submitFormikForm}
      onChangeHandler={handleChangeAndMaybeSubmit}
    />
  );
};

export default JobBasicInfo;
